import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema, generateOrganizationSchema } from '../../utils/seo';

const HomePage = () => {
  const navigate = useNavigate();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'CerviShield | India Cervical Cancer Awareness & AI Risk Assessment Platform',
    description: 'CerviShield is India\'s AI-assisted cervical cancer awareness platform providing free risk assessment, educational resources, and hospital directory. Early detection saves lives. Get personalized guidance for cervical health screening and prevention.',
    keywords: 'cervical cancer awareness, cervical health India, HPV screening, cancer prevention, AI health assessment, women\'s health, Pap test, cervical cancer screening',
    canonical: '/',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateWebPageSchema(
          'CerviShield - Cervical Cancer Awareness & AI Risk Assessment',
          'AI-assisted cervical health guidance platform for early risk understanding and preventive healthcare access',
          '/'
        ),
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <header className="relative min-h-[100vh] sm:min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1b61360e06a36664a05ded6b0e1c5b58.jpg"
            alt="Healthcare Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/30 to-transparent lg:from-black/80 lg:via-black/20 lg:to-transparent"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 lg:py-32 mt-16 sm:mt-20 lg:mt-24">
          <div className="text-white animate-fade-in text-center lg:text-left max-w-3xl lg:max-w-2xl mx-auto lg:mx-0">
            <h1 className="text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold font-display leading-[1.1] mb-5 sm:mb-6 drop-shadow-lg">
              <span className="font-light block mb-2">Early Awareness</span>
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent drop-shadow-none">Saves Lives</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-white leading-relaxed mb-7 sm:mb-8 max-w-2xl mx-auto lg:mx-0 drop-shadow-md">
              AI-assisted cervical health guidance platform designed to support early risk understanding and improve access to preventive healthcare. Take control of your health journey today.
            </p>
            <Link
              to="/consent"
              className="inline-flex items-center gap-3 px-7 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-primary to-teal-500 text-white rounded-full text-base sm:text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl"
            >
              Start Cervical Health Check
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
            <div className="mt-5 sm:mt-6 inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-yellow-400/30 backdrop-blur-sm border border-yellow-400/50 rounded-full">
              <i className="ri-shield-check-line text-yellow-300 text-lg"></i>
              <span className="text-xs sm:text-sm text-yellow-50 font-medium">Educational & Guidance Only</span>
            </div>
          </div>
        </div>
      </header>

      <section className="py-24 bg-gradient-to-b from-white to-teal-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-gray-800 mb-4">
              Why <span className="text-primary">CerviShield</span>?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Empowering women with knowledge, guidance, and access to preventive care
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-brain-line',
                title: 'AI-Powered Assessment',
                description: 'Advanced machine learning analyzes your health data to provide personalized risk guidance based on medical research.',
                color: 'from-primary to-teal-500',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Privacy First',
                description: 'Your data is processed anonymously with no storage. We prioritize your privacy and security at every step.',
                color: 'from-accent to-purple-500',
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Early Prevention',
                description: 'Get actionable insights and guidance for early detection, screening recommendations, and preventive care steps.',
                color: 'from-secondary to-pink-500',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
              >
                <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${feature.color} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-teal-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-display">
                Understanding Your Health Journey
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Watch our comprehensive guide to cervical health screening. Learn about the importance of regular check-ups, what to expect during consultations, and how early detection can save lives.
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'ri-shield-check-line', text: 'Professional medical guidance' },
                  { icon: 'ri-heart-pulse-line', text: 'Understanding screening procedures' },
                  { icon: 'ri-calendar-check-line', text: 'When and why to get tested' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full">
                      <i className={`${item.icon} text-xl text-primary`}></i>
                    </div>
                    <span className="text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <div className="relative pt-[56.25%]">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/Vlpa41IQ-FQ"
                  title="Cervical Cancer Awareness - CerviShield"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Cervical Cancer Awareness</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Essential information about prevention and early detection
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Cervical Health', 'Screening Guide', 'Prevention', 'Early Detection'].map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-gray-800 mb-4">
              How It <span className="text-primary">Works</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Simple, fast, and confidential health assessment in three easy steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-1/3 left-1/4 right-1/4 h-1 bg-gradient-to-r from-primary via-accent to-secondary"></div>

            {[
              {
                step: '01',
                title: 'Complete Health Check',
                description: 'Answer simple questions about your health history, symptoms, and lifestyle factors in just 2-3 minutes.',
                icon: 'ri-file-list-3-line',
              },
              {
                step: '02',
                title: 'AI Analysis',
                description: 'Our machine learning model analyzes your data to assess risk factors based on medical research.',
                icon: 'ri-cpu-line',
              },
              {
                step: '03',
                title: 'Get Guidance',
                description: 'Receive personalized recommendations, educational resources, and next steps for preventive care.',
                icon: 'ri-guide-line',
              },
            ].map((step, index) => (
              <div key={index} className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 z-10">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary to-accent rounded-full mb-6 mx-auto">
                  <i className={`${step.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-5xl font-bold text-primary/10 mb-4 text-center">{step.step}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed text-center">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/consent"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-teal-500 text-white rounded-full text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Start Your Health Check Now
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-secondary/10 to-accent/10 rounded-3xl p-12 md:p-16 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-secondary rounded-full mx-auto mb-6">
              <i className="ri-alarm-warning-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-display text-gray-800 mb-4">
              Need Immediate Help?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              If you're experiencing severe symptoms or need urgent medical attention, please seek immediate care.
            </p>
            <Link
              to="/emergency"
              className="inline-flex items-center gap-3 px-8 py-4 bg-secondary text-white rounded-full text-lg font-semibold hover:bg-secondary/90 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View Emergency Guidance
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
